import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { MultiSelectDropdown } from './components/Dropdowns';
import { switches } from './dataStore';
import SwitchInfos from './SwitchCatalogData';
import { AudioPlayer } from './components/AudioProvider';
import { Button } from './components/Button';


// Manually set x/y positions for each switch by id
const manualPositions = {
  'u4t': { x: 20, y: 80 },
  'mxblack': { x: 42, y: 57 },
  'nixie': { x: 40, y: 50 },
  'doom': { x: 55, y: 92 },
  'milky_yellow': { x: 18, y: 67 },
  'smoothie': { x: 84, y: 29 },
  'caramel_latte': { x: 51, y: 67 }, //move down a bit
  'cloud': { x: 36, y: 54 },
  'cheese': { x: 57, y: 85 },
  'hyacinth_v2': { x: 27, y: 88 },
  'macchiato': { x: 63, y: 35 },
  'sugar_rush': { x: 65, y: 79 },
  'sunset_gleam': { x: 80, y: 56 },
  'xinhai': { x: 71, y: 92 },
  'yogurt': { x: 74, y: 93 },
  'purple_dawn': { x: 25, y: 61 },
  'uni': { x: 68, y: 39 },
  'y1x': { x: 73, y: 84 },
  'y3': { x: 41, y: 73 },
  'mz_z1': { x: 90, y: 21 },
  'poro': { x: 34, y: 66 },
  'cosmic_purple': { x: 86, y: 58 },
  'lunar_stone': { x: 69, y: 76 },
  'yuying_linear': { x: 61, y: 67 },
  'yuying_tactile': { x: 55, y: 63 },
  'yunie_linear': { x: 34, y: 32 },
  'yunie_tactile': { x: 44, y: 44 },
};

const scratchIndex = {
    'mxblack': 8,
    'nixie': 9.5,
    'doom': 0,
    'milky_yellow': 0,
    'smoothie': 0,
    'caramel_latte': 0,
    'cloud': 0,
    'cheese': 0,
    'hyacinth_v2': 0,
    'macchiato': 0,
    'sugar_rush': 0,
    'sunset_gleam': 0,
    'xinhai': 0,
    'yogurt': 0,
    'purple_dawn': 0,
    'uni': 0,
    'y3': 0,
    'mz_z1': 0,
    'poro': 0,
    'cosmic_purple': 2,
    'lunar_stone': 1.5,
    'yuying_linear': 6,
    'yuying_tactile': 7,
};

const switchIds = Object.keys(switches).filter(id => switches[id].visible);
const switchData = switchIds.map((id) => {
    const sw = switches[id];
    const pos = manualPositions[id] || { x: 50, y: 50 }; // Default if not set
    const detailed = SwitchInfos[id] || {};
    const has1 = !!detailed.soundtest1;
    const has2 = !!detailed.soundtest2;
    return {
        id: sw.id,
        name: sw.name,
        manufacturer: sw.manufacturer,
        type: sw.type,
        x: pos.x,
        y: pos.y,
        scratchIndex: scratchIndex[id] || -1,
        has1,
        has2,
        vis: true
    };
});

const matchesSoundTest = (sw, selectedSoundTests) => {
    if (selectedSoundTests.includes('both')) return sw.has1 && sw.has2;
    if (selectedSoundTests.includes('soundtest1') && selectedSoundTests.includes('soundtest2')) return sw.has1 || sw.has2;
    if (selectedSoundTests.includes('soundtest1')) return sw.has1;
    if (selectedSoundTests.includes('soundtest2')) return sw.has2;
    return false;
};


const manufacturerOptions = Array.from(new Set(switchData.map((sw) => sw.manufacturer))).map((m) => ({ id: m, name: m }));
const typeOptions = Array.from(new Set(switchData.map((sw) => sw.type))).map((t) => ({ id: t, name: t }));
const soundTestOptions = [
    { id: 'soundtest1', name: 'Neo65 Cu' },
    { id: 'soundtest2', name: 'Mode Encore' },
    { id: 'both', name: 'Both' }
];


const SwitchCatalogPage = () => {
    const [selectedManufacturers, setSelectedManufacturers] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedSoundTests, setSelectedSoundTests] = useState([]);
    const [selectedSwitch, setSelectedSwitch] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showNames, setShowNames] = useState(true);
    const [hoveredId, setHoveredId] = useState(null);
    const [hoveredId1D, setHoveredId1D] = useState(null);
    const [shouldRender, setShouldRender] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [infoVisible, setInfoVisible] = useState(false);
    const [infoShouldRender, setInfoShouldRender] = useState(false);

    useEffect(() => {
        if (showModal) {
            setShouldRender(true);
            const timeout = setTimeout(() => setModalVisible(true), 10);
            return () => clearTimeout(timeout);
        } else {
            setModalVisible(false);
            const timeout = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showModal]);

    useEffect(() => {
        if (showInfo) {
            setInfoShouldRender(true);
            const timeout = setTimeout(() => setInfoVisible(true), 10);
            return () => clearTimeout(timeout);
        } else {
            setInfoVisible(false);
            const timeout = setTimeout(() => setInfoShouldRender(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showInfo]);

    const visibleDots = switchData.filter(sw =>
        (selectedManufacturers.length === 0 || selectedManufacturers.includes(sw.manufacturer)) &&
        (selectedTypes.length === 0 || selectedTypes.includes(sw.type)) &&
        (selectedSoundTests.length === 0 || matchesSoundTest(sw, selectedSoundTests))
    );
    const xVals = visibleDots.map(sw => sw.x);
    const yVals = visibleDots.map(sw => sw.y);

    const handleClick = (data) => {
        const sw = switches[data.id];
        const detailed = SwitchInfos[data.id] || {};
        const switchData = { ...sw, ...detailed};
        setSelectedSwitch(switchData);
        setShowModal(true);
    };    
    
    return (
        <div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden'>
            {/* Ambient blurred backdrop */}
            <div
                aria-hidden='true'
                className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
                style={{
                    backgroundImage: "url('/pictures/articles/wingtree/banner.JPG')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(12px) brightness(0.3)',
                    WebkitFilter: 'blur(12px) brightness(0.3)',
                    zIndex: 0,
                }}
            />
            <div className='relative z-10'>
                <h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>Switch Catalog</h1>
                <p className='text-gray-300 mb-5 text-center text-base max-w-5xl mx-auto'>
                    Take a look at our switch catalog, featuring a <b>Volume/Pitch</b> graph and a <b>Scratch Index</b> graph. Click on any switch in the chart to see more information and hear sound tests where available. Only switches with an <b>audible scratch/textured feel</b> are included in the Scratch Index graph. 

                </p>
                <div className="mb-12 flex gap-4 justify-center flex-wrap">
                    <Button onClick={() => { setShowInfo(true); }} children="DISCLAIMER" className="min-w-[180px] max-w-[180px]"/>
                </div>
                {/* Filter Dropdowns */}
                <div className="mb-8 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-center">
                    <MultiSelectDropdown
                        label="Manufacturer"
                        options={manufacturerOptions}
                        selected={selectedManufacturers}
                        setSelected={setSelectedManufacturers}
                        enableSearch={false}
                        className="min-w-[180px]"
                    />
                    <MultiSelectDropdown
                        label="Switch Type"
                        options={typeOptions}
                        selected={selectedTypes}
                        setSelected={setSelectedTypes}
                        enableSearch={false}
                        className="min-w-[180px]"
                    />
                    <MultiSelectDropdown
                        label="Sound Test Availability"
                        options={soundTestOptions}
                        selected={selectedSoundTests}
                        setSelected={setSelectedSoundTests}
                        enableSearch={false}
                        className="sm:max-w-[230px]"
                    />
                    <Button onClick={() => setShowNames(!showNames)} children={`${showNames ? 'Hide' : 'Show'} Names`} className="min-w-[180px] max-w-[180px]"  />                        
                    <Button onClick={() => { setSelectedManufacturers([]); setSelectedTypes([]); setSelectedSoundTests([]); setShowNames(true) }} children="Reset Filters" className="min-w-[140px] max-w-[140px]" />
                </div>
                {/* Volume/Pitch Chart */}
                {/* items-center w-full h-[70vh] rounded-lg bg-black/40 p-3 add this for background maybe */}
                <div className="flex justify-center"> 
                    <ResponsiveContainer width="95%" height={600}>
                        <ScatterChart margin={{ left: 5, right: 0, top: 0, bottom: 20 }} className="no-outline">
                        <XAxis
                            type="number"
                            dataKey="x"
                            name="Volume"
                            allowDataOverflow={true}
                            domain={[Math.min(...xVals) - 5, Math.max(...xVals) + 5]}
                            tick={false}
                            axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }}
                            label={{ value: 'Volume', position: 'insideBottom', offset: -20, style: { fill: '#d1d5db', fontSize: 20 } }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            name="Pitch"
                            allowDataOverflow={true}
                            domain={[Math.min(...yVals) - 5, Math.max(...yVals) + 5]}
                            tick={false}
                            axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }}
                            label={{ value: 'Pitch', angle: -90, position: 'insideLeft', style: { fill: '#d1d5db', fontSize: 20 } }}
                        />
                        <Tooltip isAnimationActive={false} cursor={{ strokeDasharray: "3 3" }} content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                const sw = payload[0].payload;
                                return (
                                    <div style={{ background: '#222', color: '#d1d5db', padding: '8px 12px', borderRadius: '8px', border: '1px solid #444' }}>
                                        <div><strong>{sw.name}</strong></div>
                                        <div>Manufacturer: {sw.manufacturer}</div>
                                        <div>Type: {sw.type}</div>
                                        <div>Volume: {sw.x}, Pitch: {sw.y}</div>
                                    </div>
                                );
                            }
                            return null;
                        }} />
                        <Scatter
                            data={switchData}
                            fill="#8884d8"
                            onClick={handleClick}
                            shape={props => {
                                const { cx, cy, payload } = props;
                                const isVisible = visibleDots.some(sw => sw.id === payload.id);
                                if (!isVisible) return null;
                                return (
                                    <g>
                                        <circle
                                            key={payload.id}
                                            cx={cx}
                                            cy={cy}
                                            r={7}
                                            fill={hoveredId === payload.id ? '#746ed2ff' : '#9e99f6ff'}
                                            stroke={hoveredId === payload.id ? '#dededed2' : '#ffffffd2'}
                                            strokeWidth={2}
                                            onMouseEnter={() => setHoveredId(payload.id)}
                                            onMouseLeave={() => setHoveredId(null)}
                                        />
                                        {showNames && (
                                            <text
                                                x={cx}
                                                y={cy - 13}
                                                textAnchor="middle"
                                                fill="#d1d5db"
                                                fontSize="10"
                                                fontWeight="bold"
                                            >
                                                {payload.name}
                                            </text>
                                        )}
                                    </g>
                                );
                            }}
                            isAnimationActive={true}
                        />
                    </ScatterChart>
                    </ResponsiveContainer>
                </div>
                {/* Scratch Index Chart */}
                <div className="flex justify-center mt-6">
                    <ResponsiveContainer width="95%" height={200}>
                        <ScatterChart 
                            data={switchData.filter(sw => sw.scratchIndex > 0)} 
                            margin={{ left: 60, right: 0, top: 20, bottom: 20 }}
                            className="no-outline"    
                        >
                            <XAxis
                                type="number"
                                dataKey="scratchIndex"
                                domain={[0, 10]}
                                allowDataOverflow={true}
                                tick={false}
                                axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }}
                                label={{ value: 'Scratch Index', position: 'insideBottom', offset: -20, style: { fill: '#d1d5db', fontSize: 20 } }}
                            />
                            <ReferenceLine stroke="transparent" x={0} label={{ value: 'Smooth', dx: 28, offset: -22, position: 'insideBottom', style: { fill: '#d1d5db', fontSize: 16 } }} />
                            <ReferenceLine stroke="transparent" x={10} label={{ value: 'Scratchy', dx: -34, offset: -22, position: 'insideBottom', style: { fill: '#d1d5db', fontSize: 16 } }} />
                            <YAxis hide domain={[0, 1]} />
                            <Tooltip
                                cursor={false}
                                isAnimationActive={false}
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const sw = payload[0].payload;
                                        const scratchIndex = sw.scratchIndex;
                                        return (
                                            <div style={{ background: '#222', color: '#d1d5db', padding: '8px 12px', borderRadius: '8px', border: '1px solid #444' }}>
                                                <div><strong>{sw.name}</strong></div>
                                                <div>Scratch Index: {scratchIndex}</div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Scatter
                                dataKey={() => 0.5}
                                fill="#9e99f6ff"
                                onClick={handleClick}

                                shape={props => {
                                    const { cx, cy, payload } = props;
                                    const isVisible = visibleDots.some(sw => sw.id === payload.id);
                                    if (!isVisible) return null;
                                    return (
                                        <g>
                                            <circle
                                                key={payload.id}
                                                cx={cx}
                                            cy={cy}
                                            r={7}
                                            fill={hoveredId1D === payload.id ? '#746ed2ff' : '#9e99f6ff'}
                                            stroke={hoveredId1D === payload.id ? '#dededed2' : '#ffffffd2'}
                                            strokeWidth={2}
                                            onMouseEnter={() => setHoveredId1D(payload.id)}
                                            onMouseLeave={() => setHoveredId1D(null)}
                                        />
                                        {showNames && (
                                            <text
                                                x={cx}
                                                y={cy - 13}
                                                textAnchor="middle"
                                                fill="#d1d5db"
                                                fontSize="10"
                                                fontWeight="bold"
                                            >
                                                {payload.name}
                                            </text>
                                        )}
                                    </g>
                                );
                            }}
                            />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Modal */}
            {shouldRender && selectedSwitch && (
                <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300 ${modalVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setShowModal(false)}>
                    <div className={`bg-[#111112] p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] transition-all duration-300 ${modalVisible ? 'scale-100' : 'scale-95'}`} onClick={(e) => e.stopPropagation()}>
                        <style>{`.modal-content::-webkit-scrollbar { display: none; }`}</style>
                        <div className="modal-content overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', maxHeight: '70vh' }}>
                        <div className="flex justify-between items-center mb-4">
                            <a href={selectedSwitch.link} target="_blank" rel="noopener noreferrer" className="text-2xl font-bold text-white relative after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition px-0 mt-1">{selectedSwitch.name}</a>
                            <button
                                className="p-1 bg-transparent border-none opacity-70 hover:opacity-100"
                                onClick={() => setShowModal(false)}
                                style={{ lineHeight: 0 }}
                            >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="pointer-events-none text-white drop-shadow-md">
                                    <line x1="8" y1="8" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <line x1="20" y1="8" x2="8" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="text-gray-300 space-y-4">
                            <div><strong>Volume:</strong> {manualPositions[selectedSwitch.id].x}</div>
                            <div><strong>Pitch:</strong> {manualPositions[selectedSwitch.id].y}</div>
                            <div><strong>Manufacturer:</strong> {selectedSwitch.manufacturer}</div>
                            <div><strong>Type:</strong> {selectedSwitch.type}</div>
                            <div><strong>Price:{' '}</strong>
                            <a href={selectedSwitch.link} target="_blank" rel="noopener noreferrer" className="relative after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition px-0 mt-1">${selectedSwitch.price} Per Switch</a>
                            </div>
                            {selectedSwitch.stemMaterial && <div><strong>Stem Material:</strong> {selectedSwitch.stemMaterial}</div>}
                            {selectedSwitch.topHousing && <div><strong>Top Housing:</strong> {selectedSwitch.topHousing}</div>}
                            {selectedSwitch.bottomHousing && <div><strong>Bottom Housing:</strong> {selectedSwitch.bottomHousing}</div>}
                            {selectedSwitch.springWeight && <div><strong>Detailed Spring Weight:</strong> {selectedSwitch.springWeight}</div>}
                            {selectedSwitch.springLength && <div><strong>Spring Length:</strong> {selectedSwitch.springLength}mm</div>}
                            {selectedSwitch.totalTravel && <div><strong>Total Travel:</strong> {selectedSwitch.totalTravel}mm</div>}
                            {selectedSwitch.soundtest1 && (
                                <div>
                                    <strong>Neo65 Cu Sound Test:</strong>
                                    <AudioPlayer src={selectedSwitch.soundtest1} className='w-full mt-2' />
                                </div>
                            )}
                            {selectedSwitch.soundtest2 && (
                                <div>
                                    <strong>Mode Encore Sound Test:</strong>
                                    <AudioPlayer src={selectedSwitch.soundtest2} className='w-full mt-2' />
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            )}

            {/* info Modal */}
            {infoShouldRender && (
                <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300 ${infoVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setShowInfo(false)}>
                    <div className={`bg-[#111112] p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] transition-all duration-300 ${infoVisible ? 'scale-100' : 'scale-95'}`} onClick={(e) => e.stopPropagation()}>
                        <style>{`.modal-content::-webkit-scrollbar { display: none; }`}</style>
                        <div className="modal-content overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', maxHeight: '70vh' }}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">DISCLAIMER</h2>
                            <button
                                className="p-1 bg-transparent border-none opacity-70 hover:opacity-100"
                                onClick={() => setShowInfo(false)}
                                style={{ lineHeight: 0 }}
                            >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="pointer-events-none text-white drop-shadow-md">
                                    <line x1="8" y1="8" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <line x1="20" y1="8" x2="8" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="text-gray-300 space-y-4">
                            <p className='text-gray-300 mb-12 text-left text-lg max-w-5xl mx-auto'>
                                This graph is <b>SUBJECTIVE</b> based on my recording conditions. You <b>MAY</b> experience different results. Slight <b>variations</b> in sound tests may occur due to differences in <b>mic placements</b>.
                                <br /><br />
                                Clicking on the <b>switch names</b> will take you to the switch's product page for easy reference. Some links may be <b>affiliate links</b> that help support my channel at no extra cost to you.
                                <br /><br />
                                <b>Standardized Sound Test 1</b> is done with the <b>Neo65 Cu</b> with a <b>Polypropylene</b> plate, <b>gasket-mounted</b> with <b>no foams</b> on a <b>wooden</b> desk with a deskmat. Recorded with a <b>Fifine AM8 Dynamic Mic</b>.
                                <br /><br />
                                <b>Standardized Sound Test 2</b> is done with the <b>Mode Encore</b> with an <b>Aluminum</b> plate, <b>green lattice blocks</b> with <b>no foams</b> on a <b>glass</b> desk with a deskmat. Recorded with a <b>Shure SM57 Cardioid Dynamic Mic</b>.
                                <br /><br />
                                For the most accurate impression, I recommend <b>using headphones</b> and comparing switches only <b>within the same sound test</b>. The two tests are meant to illustrate how a switch performs in different contexts, not for cross-examination.
                            </p>
                        </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SwitchCatalogPage;


